<template>
	<div class="page-with-tabs">
		<!--
			两级 Tabs（仅最外层 PageTabs 渲染）：
			- 一级：顶层分组 children
			- 二级：当前一级节点下所有“功能页面(type=2)”递归展开后的扁平列表
		-->
		<div v-if="showHeader" class="tabs-header">
			<div v-if="showTopTabs" class="tabs-row"  style="border-bottom: 0.1px solid hsla(var(--hsl-text), 0.12);">
				<!-- elevation -->
				<var-tabs		
					:style="{ Height: tabsHeight + 'px' ,paddingtop: '10px'}"
					class="top-tabs"
					v-model:active="active1"
					@change="onTopChange"
					title-scroll
					:ellipsis="false"
					:title-gutter="8"
					:active-color="'var(--color-primary)'"
					:inactive-color="'hsla(var(--hsl-text), 0.6)'"
				>
					<var-tab v-for="(n, idx) in topTabs" :key="n.name || idx">
						{{ n.meta?.title || `Tab ${idx + 1}` }}
					</var-tab>
				</var-tabs>
			</div>
			<div v-if="showSubTabs" class="tabs-row" >
				<var-tabs
					class="sub-tabs"
					v-model:active="active2"
					@change="onSubChange"
					title-scroll
					:ellipsis="false"
					:title-gutter="8"
					:active-color="'var(--color-primary)'"
					:inactive-color="'hsla(var(--hsl-text), 0.6)'"
				>
					<var-tab class="sub-tab" v-for="(n, idx) in subTabs" :key="n.name || idx">
						{{ n.meta?.title || `Tab ${idx + 1}` }}
					</var-tab>
				</var-tabs>
			</div>
		</div>

		<!--
			内容区只保留一个 RouterView。
			如果把 RouterView 放到 var-tabs-items / var-tab-item 里并用 active 索引控制显示，
			点击一级 Tab 时 active1 会先变化，导致当前路由组件先被卸载/重建一次，随后 router.push 再切到目标路由，表现为“初始化两次”。
		-->
		<div class="tabs-content">
			<RouterView v-slot="{ Component, route: r }">
		<!-- 使用 r.name：同一路由在 query/hash 变化时尽量复用组件，避免不必要的卸载/重建。强制重建页面”，可以改回 :key="r.fullPath"。-->
				<component v-if="Component" :is="Component" :key="r.name" />
				<div v-else class="empty">暂无页面内容</div>
			</RouterView>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, nextTick, inject, provide, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from 'd3components';
import { getKeyStr ,removeKey, setKey, setKeyJsonStr ,getKeyJson,updateKeyJson,getsubKey, removeKeys} from 'd3components/src/utils/localStorage';


const route = useRoute();
const router = useRouter();
const store = useUserStore();

// tabs高度
const tabsHeight = computed(() => (store as any).erpConfig?.sa_menu_top_height || 45);

// ========== 核心数据：路由层级与菜单 ==========
// pageTabsDepth: 当前 PageTabs 的嵌套层级 (0=顶层, 1=第二层...)
const pageTabsDepth = inject<number>('pageTabsDepth', 0);
provide('pageTabsDepth', pageTabsDepth + 1);

// hidePageTabsHeader: 是否隐藏 tabs (侧边栏模式下为 true)
const hidePageTabsHeader = inject<any>('hidePageTabsHeader', true);

// tab 状态持久化（不做兼容扩展，只保留当前项目用到的 key）
const keyOf = (r: any) => (r?.meta?.mid ?? r?.name ?? r?.path) as any;
const getState = (k: string) => (getsubKey('mobile', 'user_info')?.[0] as any)?.tabStates?.[k];
const setState = (k: string, v: string) => {
	const list = (getsubKey('mobile', 'user_info') || []) as any[];
	if (!list[0]) return;
	list[0] = { ...list[0], tabStates: { ...(list[0].tabStates || {}), [k]: v } };
	updateKeyJson('mobile', 'user_info', list.slice(0, 10));
};

// =====================================================================================
// 一级：顶层分组 children
// 二级：当前一级节点下递归展开的“功能页面(type=2)”
// =====================================================================================
// 路由层级中的分组
const groups = computed(() => route.matched.filter((r: any) => r.meta?._isGroup));
// 顶层分组
const topGroup = computed(() => groups.value[0]);


 //Tabs 的 可见性- meta.v === false: 从 Tabs 列表中隐藏（用于无 tab 入口但仍可通过路由到达的页面”）
// meta.single === true: 进入该页面时隐藏整个 Tabs 头部（只展示内容区）

const isVisible = (n: any) => n?.meta?.v !== false;

// 顶层分组的 children
const topTabsAll = computed<any[]>(() => (topGroup.value?.children || []) as any[]);
// UI 展示用的一级 Tabs（过滤掉 v:false）
const topTabs = computed<any[]>(() => topTabsAll.value.filter(isVisible));
// 一级 Tabs 的当前选中
const active1 = ref(0);
// 二级 Tabs 的当前选中
const active2 = ref(0);
// 递归获取节点的子节点
const childrenOf = (n: any) => (n?.children || []) as any[];
// 判断节点是否为动态页面
// const hasDynPage = (n: any) => !!(n?.meta?.mid && n?.meta?.page);
const hasDynPage = (n: any) => !!(n?.meta?.page);  //改成和web项目统一，有 page 就能成为功能页”，不再被 mid 卡住
// 二级 Tab：递归收集所有带动态页面配置（mid/page）的节点
const collectPagesAll = (n: any): any[] => {
	if (!n) return [];
	// 如果当前节点是动态页面，则添加到结果中
	const self = hasDynPage(n) ? [n] : [];
	// 递归收集子节点中的动态页面
	return [...self, ...childrenOf(n).flatMap(collectPagesAll)];
};
// UI 展示用的二级 Tabs（过滤掉 v:false）
const collectPagesVisible = (n: any): any[] => collectPagesAll(n).filter(isVisible);

// 当前一级 Tabs 的二级 Tabs（注意：这里用于 UI 展示）
const subTabs = computed<any[]>(() => collectPagesVisible(topTabs.value[active1.value]));
// 当前一级 Tabs 的二级功能页全集（用于路由合法性判断/重定向兜底，不能过滤 v:false）
const subTabsAll = computed<any[]>(() => collectPagesAll(topTabsAll.value.find((t) => String(t?.name ?? '') === String(topTabs.value[active1.value]?.name ?? ''))));

// 二级 tab 数量 <= 1 时隐藏二级 tabs 行
const showSubTabs = computed(() => subTabs.value.length > 1);

// 一级 Tabs 默认显示；当“一级=1 且 二级=1 且标题相同”时折叠一级，避免两行重复
const showTopTabs = computed(() => {
	if (topTabs.value.length !== 1) return topTabs.value.length > 0;
	const onlyTop = topTabs.value[0];
	const pages = collectPagesVisible(onlyTop);
	if (pages.length !== 1) return true;
	const topTitle = String(onlyTop?.meta?.title ?? '');
	const subTitle = String(pages[0]?.meta?.title ?? '');
	if (!topTitle || !subTitle) return true;
	return topTitle !== subTitle;
});

// 当前页面为 single（隐藏 tabs header，只展示内容区）
const isSinglePage = computed(() => (route.meta as any)?.single === true);
// 当前页面为隐藏（v:false）—— 用于不展示 tab，但路由可到达
const isHiddenByMetaV = computed(() => (route.meta as any)?.v === false);

// 仅最外层显示两级 Tabs，避免嵌套 Tabs 越钻越深
const showHeader = computed(() => {
	const hide = typeof hidePageTabsHeader === 'object' ? hidePageTabsHeader.value : hidePageTabsHeader;
	// meta.single / meta.v=false 时：强制隐藏 tabs header
	if (isSinglePage.value || isHiddenByMetaV.value) return false;
	return !hide && pageTabsDepth === 0;
});

// 避免 watch(syncTabs) 与 onChange(router.push) 相互触发形成循环
let syncing = false;

function syncTabs() {
	// 标记为“同步中”，避免 watch 与 onChange 互相递归
	syncing = true;
	// 当前路由 name，用于定位一级/二级选中
	const name = String(route.name ?? '');
	// 一级 Tabs 列表：
	// - tabsAll: 用于路由定位（包含 v:false 的隐藏项）
	// - tabs: 用于 UI 显示（过滤 v:false）
	const tabsAll = topTabsAll.value;
	const tabs = topTabs.value;
	// 用于 localStorage 的 key：不同顶层菜单有不同的记忆空间
	const topKey = String(keyOf(topGroup.value) ?? '');

	// 先用当前路由反推 active1：看当前路由属于哪个一级分组
	let tIdxAll = tabsAll.findIndex((t) => {
		if (String(t?.name ?? '') === name) return true;
		return collectPagesAll(t).some((p) => String(p?.name ?? '') === name);
	});
	// 路由匹配不到时，回退到上次记忆的一级选中
	if (tIdxAll < 0 && topKey) {
		const saved = parseInt(getState(`lastTabIndex:${topKey}`) || '');
		tIdxAll = !isNaN(saved) && saved >= 0 && saved < tabsAll.length ? saved : 0;
	}
	// 将“全集索引”映射到“可见索引”（如果当前一级是 v:false，则 UI 不显示，但内容仍可渲染）
	const currentTopName = String(tabsAll[tIdxAll]?.name ?? '');
	const tIdxVisible = tabs.findIndex((t) => String(t?.name ?? '') === currentTopName);
	// clamp
	active1.value = Math.max(0, Math.min(tIdxVisible < 0 ? 0 : tIdxVisible, Math.max(0, tabs.length - 1)));

	// 当前一级分组下的二级功能页列表
	const pagesAll = collectPagesAll(tabsAll[tIdxAll]);
	const pages = pagesAll.filter(isVisible);
	// 在二级列表里定位当前路由对应的索引
	let pIdx = pages.findIndex((p) => String(p?.name || '') === name);
	// 路由匹配不到时，回退到上次记忆的二级选中
	if (pIdx < 0 && topKey) {
		const saved = parseInt(getState(`lastSubTabIndex:${topKey}`) || '');
		pIdx = !isNaN(saved) && saved >= 0 && saved < pages.length ? saved : 0;
	}
	// clamp 到合法范围
	active2.value = Math.max(0, Math.min(pIdx < 0 ? 0 : pIdx, Math.max(0, pages.length - 1)));
	// 延迟结束“同步中”，避免 v-model 触发的 change 被当作用户操作
	nextTick(() => {
		syncing = false;
	});
}
// 顶部 children 本身没 page 时自动跳到第一个有 page 的子页面
function ensureValidPageRoute() {
	// meta.single / meta.v=false：只展示内容区，不做 tabs 兜底重定向
	if (isSinglePage.value || isHiddenByMetaV.value) return;
	// 这里不能用 UI 的 subTabs（会过滤 v:false），否则会把“隐藏 tab 但可到达的页面”误判为无效并重定向
	const pages = (subTabsAll.value || []) as any[];
	if (!pages.length) return;
	const currentName = String(route.name ?? '');

	// 当前路由已经是某个功能页，无需处理
	if (pages.some((p) => String(p?.name ?? '') === currentName)) return;

	//检查当前路由是否在当前层级的所有子路由中（包括没有page配置的路由）
	// 并且该路由有基本的页面配置（至少有name）
	const currentTopTab = topTabs.value[active1.value];
	const allChildRoutes = currentTopTab?.children || [];
	const currentRoute = allChildRoutes.find((child: any) => String(child?.name ?? '') === currentName);

	// 只有当路由在子路由列表中且有基本配置时才跳过重定向
	if (currentRoute && (currentRoute.meta?.page || currentRoute.name)) {
		console.log(`路由 ${currentName} 在子路由列表中且有基本配置，跳过重定向`);
		return;
	}

	// 当前路由是分组/占位路由时，自动跳到该分组下第一个功能页
	console.log(`路由 ${currentName} 不在有效页面中，将重定向到第一个页面: ${pages[0]?.name}`);
	const first = pages[0];
	if (first?.name) router.replace({ name: first.name });
}

// route / topTabs 变化时，同步 active1/active2（nextTick 确保 subTabs 先更新完成）
watch(
	() => `${String(route.name ?? '')}|${topTabs.value.map((t) => String(t?.name ?? '')).join('|')}`,
	() =>
		nextTick(() => {
			syncTabs();
			ensureValidPageRoute();
		}),
	{ immediate: true }
);

function onTopChange(i: number | string) {
	// 用户切换一级：只处理用户触发的 change
	if (syncing) return;
	// 更新一级选中
	const idx = typeof i === 'string' ? parseInt(i) : i;
	active1.value = isNaN(idx) ? 0 : idx;
	// 切一级后，跳到该分组下第一个功能页（让二级与内容区跟随路由自然同步）
	const first = collectPagesVisible(topTabs.value[active1.value])[0];
	if (first?.name) router.push({ name: first.name });
}
onMounted(() => {
  console.log('mounted')
})

onUnmounted(() => {
  console.log('unmounted')
   console.log('PageTabs destroyed')
})
function onSubChange(i: number | string) {
	// 用户切换二级：can-swipe 与点击二级 Tab 都会触发
	if (syncing) return;
	// 定位目标二级功能页
	const idx = typeof i === 'string' ? parseInt(i) : i;
	const page = subTabs.value[idx];
	if (!page?.name) return;

	// 按“顶层菜单”维度记录选中态
	const topKey = String(keyOf(topGroup.value) ?? '');
	if (topKey) {
		// 记忆一级/二级索引，用于刷新/回退时恢复
		setState(`lastTabIndex:${topKey}`, String(active1.value));
		setState(`lastSubTabIndex:${topKey}`, String(idx));
	}

	// 最终以路由为准：推动 URL/route.matched 与内容区同步
	router.push({ name: page.name });
	
}
</script>

<style scoped>
.page-with-tabs {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.tabs-header {
	position: sticky;
	top: 0;
	flex-shrink: 0;
	background: var(--color-body);
	z-index: 10;
	/* 使用现有文字颜色变量做阴影与分割线，兼容明暗模式 */
	/* box-shadow: 0 2px 8px hsla(var(--hsl-text), 0.10);
	border-bottom: 1px solid hsla(var(--hsl-text), 0.08); */

	/* margin-bottom: 8px; 移除margin，让内容区域占满 */
}

.tabs-row {
	width: 100%;
}

.tabs-content {
	width: 100%;
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
	/* padding-bottom: var(--bottom-bar-h); */
}

.empty {
	color: #333;
	line-height: 40px;
	text-align: center;
	background: #fff;
	display: block;
	padding: 18% 20%;
}

.single-page-content {
	width: 100%;
	/* height: calc(100vh - 80px); */
	overflow-y: auto;
}

.tab-page-content {
	width: 100%;
	height: 100%;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
}

.top-tabs:deep(.var-tabs--layout-horizontal-indicator-inner) {
	background-color: transparent !important;
	height: 20px !important;
	border-radius: 5px !important;
	/* 指示条平移动画：放慢速度 + 更“平”的移动（linear） */
	transition: transform 300ms linear !important;
	/* transform: scaleX(0.8); */
	transform-origin: center;
	transform: translateY(-5px);
	position: relative;
	overflow: visible;
}

/* Varlet 实际负责左右移动的是 indicator 容器本身（不同版本类名可能略有差异），直接覆盖 */
.top-tabs:deep(.var-tabs__indicator) {
	transition: transform 300ms linear !important;
}
.var-tabs {
    border-radius: var(--tabs-radius);
    background: var(--color-body);
    overflow: hidden;
    /* -webkit-transition: background-color 0.25s; */
    /* transition: background-color 0.25s; */
	/* border-bottom: 1px solid hsla(var(--hsl-text), 0.12); */
	border-radius: 0 !important;
}

.top-tabs:deep(.var-tabs--layout-horizontal-indicator-inner)::after {
	content: '' !important;
	position: absolute !important;
	width: 40px;
	height: 5px;
	top: 50%;
	left: 50%;
	background-color: var(--color-primary);
	border-radius: 999px !important;
	transform: translate(-50%,30%) !important;
	transition: none !important;
	pointer-events: none !important;
	box-sizing: border-box !important;
}

.sub-tabs:deep(.var-tabs--layout-horizontal-indicator) {
	display: none !important;
}
.sub-tabs:deep(.var-tabs--layout-horizontal-indicator-inner) {
	display: none !important;
}
.sub-tabs:deep(.var-tabs__indicator) {
	display: none !important;
}

.sub-tabs:deep(.var-tabs__scroller) {
	overflow-x: auto !important;
	padding: 8px 12px;
	box-sizing: border-box;
}

.sub-tabs:deep(.var-tabs__tab) {
	flex: 0 0 auto !important;
	white-space: nowrap !important;
	min-width: auto !important;
	/* height: 34px; */
	padding: 0 14px;
	margin: 0 8px 0 0;
	border-radius: 18px;
	background: hsla(var(--hsl-text), 0.08);
	color: var(--color-on-surface-variant, var(--color-text));
	border: 1px solid hsla(var(--hsl-text), 0.10);
	/* border-bottom: none !important; */
	font-weight: 500;
	box-sizing: border-box;
}

.sub-tabs:deep(.var-tabs__tab.var-tabs__tab--active) {
	background: var(--color-primary-container);
	color: var(--color-on-primary-container) !important;
	border-color: hsla(var(--hsl-text), 0.14);
	box-shadow: none !important;
	font-weight: 600;
}

::v-deep(.var-tabs::-webkit-scrollbar) {
	display: none;
}

.top-tabs:deep(.var-tabs),
.sub-tabs:deep(.var-tabs) {
	width: 100%;
	overflow-x: auto !important;
	-webkit-overflow-scrolling: touch;
	background-color: var(--color-body) !important;
	border-radius: 0 !important;
}

.top-tabs:deep(.var-tabs__scroller),
.sub-tabs:deep(.var-tabs__scroller),
.top-tabs:deep(.var-tabs__tab-wrap),
.sub-tabs:deep(.var-tabs__tab-wrap) {
	border-radius: 0 !important;
}

.top-tabs:deep(.var-tabs__tab-wrap),
.sub-tabs:deep(.var-tabs__tab-wrap) {
	display: flex !important;
	flex-wrap: nowrap !important;
	width: auto !important;
}

.top-tabs:deep(.var-tabs__tab) {
	flex: 0 0 auto !important;
	white-space: nowrap !important;
	min-width: 25% !important;
}

.top-tabs:deep(.var-tabs__scroller),
.sub-tabs:deep(.var-tabs__scroller) {
	overflow-x: auto !important;
}
.sub-tab {
  flex: 0 0 auto !important;
  white-space: nowrap !important;
  min-width: auto !important;
  margin: 15px 15px 15px 0;
  border-radius: 5px;
  background: hsla(var(--hsl-text), 0.05);
  color: var(--color-on-surface-variant, var(--color-text));
  font-size: 24px;

}

/* 滑动 Tab 内容区域样式 */
:deep(.swipeable-tabs) {
	width: 100% !important;
	height: 100% !important;
	background-color: var(--color-body) !important;
	touch-action: pan-y;
	overflow-y: auto;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
	position: relative;
}

:deep(.var-tabs-items__track) {
	transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

:deep(.var-tab-item) {
	background-color: var(--color-body) !important;
	min-height: 200px;
	width: 100%;
	height: 100%;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
}

:deep(.swipeable-tabs) {
	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
}
</style>

